import { action, computed, makeAutoObservable, observable } from 'mobx';

import { platformInfo } from '@gem/utils';

import {
  DeviceStatus,
  IUserDevice,
  IUserDevices,
  IUserDevicesItem,
  PlatformTypesEnum,
} from 'APP/model/userSettings/userSettingsModel.types';

import { Root } from '..';

export class Sessions {
  root: Root;
  data = observable.map<string, IUserDevicesItem>({}, { deep: true });
  currentSession: IUserDevice;
  loaded = false;
  sessionTTl = null;

  constructor(root: Root) {
    makeAutoObservable(this);
    this.root = root;
  }

  @computed
  setMyCurrentSession(): void {
    this.currentSession = {
      deviceId: platformInfo.deviceId,
      platform: PlatformTypesEnum.WEB,
      device: platformInfo.device,
      osName: platformInfo.osName,
      osVersion: platformInfo.osVersion,
      appVersion: this.root.App.version,
    };
  }

  @action
  reset(): void {
    this.data.clear();
    this.loaded = false;
    this.sessionTTl = null;
  }

  setSessionTtl = (ttl: any): void => {
    this.sessionTTl = ttl;
  };

  @computed
  get sessions(): IUserDevices {
    return Array.from(this.data.values());
  }

  @computed
  get activeSessions(): IUserDevices {
    return this.sessions.filter(
      (session) =>
        !!session.device.status &&
        [DeviceStatus.Trusted, DeviceStatus.Pending, DeviceStatus.Unlocked].includes(
          session.device.status
        )
    );
  }

  getSessionByKey = (sessionKey: string): IUserDevicesItem | null => {
    return this.data.get(sessionKey) || null;
  };

  @action
  setSessionDeviceStatus = (deviceId: string, status: DeviceStatus): void => {
    const deviceItem = this.data.get(deviceId);
    if (deviceItem) {
      deviceItem.device.status = status;
      this.data.set(deviceId, deviceItem);
    }
  };

  @action
  setCurrentSession = (session: IUserDevice | undefined): void => {
    if (session) {
      this.currentSession = session;
    }
  };

  @action
  setSessions = (sessions: IUserDevices | undefined): void => {
    this.loaded = true;

    if (sessions) {
      sessions.forEach((session) => {
        this.data.set(session.device.deviceId, session);
      });
    }
  };

  @action
  addSession = (session: IUserDevicesItem): void => {
    this.data.set(session.device.deviceId, session);
  };

  @action
  removeSessions = (sessionKeys: string[]): void => {
    sessionKeys.forEach((sessionKey) => {
      if (this.data.has(sessionKey)) {
        this.data.delete(sessionKey);
      }
    });
  };

  @computed
  get sessionsForApprove(): IUserDevicesItem[] {
    return this.sessions.filter((session) => session.device.status === DeviceStatus.Pending);
  }
}
